import {
  createTheme,
  useTheme,
  PaletteColor,
  type TypographyVariantsOptions,
} from '@mui/material/styles';
// Registers MUI X picker component slots (MuiPickersToolbarText, etc.) on the core
// `Components` theme type so the picker overrides below are type-checked.
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { blend } from '@mui/system/colorManipulator';
import { Z_INDEX } from './zIndex';
import {
  MOBILE_TYPOGRAPHY,
  HealthxchangeThemeFontSizes,
  TextVariant,
  buildDesktopResponsiveTypographyOverrides,
} from './typography';
import {
  dateTimePickerToolbarStyleOverrides,
  multiSectionDigitalClockSectionStyleOverrides,
  pickersToolbarButtonStyleOverrides,
  pickersToolbarTextStyleOverrides,
} from './pickerToolbarThemeOverrides';
import { menuListStyleOverrides } from './menuThemeOverrides';
export const GRADIENTS = {
  titleDiagonal:
    'linear-gradient(148.13deg, #F7F8F9 12.33%, rgba(251, 251, 252, 0.5) 22.68%, rgba(255, 255, 255, 0) 40%)',
  cardBottomHover: 'linear-gradient(0deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 77.96%)',
  cardBottomWeak:
    'linear-gradient(0deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.1) 35%, rgba(0, 0, 0, 0) 80%)',
  skinviveOffer: 'linear-gradient(180deg, #078C99 0%, #03727C 100%)',
} as const;

const HealthxchangeThemeColours = {
  black: '#000000',
  white: '#FFFFFF',
  blue: '#3A588C',
  softBlue: '#DFEAF1',
  silver: '#F7F8F9',
  lightGray: '#D7D9Df',
  darkGray: '#6B6C74',
  extraDarkGray: '#202020',
  softGreen: '#C3E4D9',
  green: '#038258',
  yellow: '#FFC700',
  linkBlue: '#0B79A6',
  red: '#D82B2E',
  purple: '#835CD0',
  softPurple: '#E7DBFF',
  lightPurple: '#F7F3FF',
  coral: '#FFB89A',
  lightCoral: '#FFEDE6',
};

const mobileTypographyWithColours: TypographyVariantsOptions = Object.keys(
  MOBILE_TYPOGRAPHY,
).reduce((acc, key) => {
  const variant = key as keyof typeof MOBILE_TYPOGRAPHY;
  const color =
    variant === 'p3' ? HealthxchangeThemeColours.darkGray : HealthxchangeThemeColours.black;
  return { ...acc, [variant]: { ...MOBILE_TYPOGRAPHY[variant], color } };
}, {} as TypographyVariantsOptions);

const defaultConfig = createTheme({
  typography: {
    fontFamily: 'Albert Sans',
    fontSize: 14,
    // Mobile-first styles (xs). Desktop overrides are applied in the second createTheme call.
    ...mobileTypographyWithColours,
  },
  palette: {
    header: {
      globalNavBackground: '#222222',
    },
    background: {
      default: HealthxchangeThemeColours.white,
      paper: HealthxchangeThemeColours.white,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: (themeParam) => ({
        p: {
          ...themeParam.typography.p,
        },
        button: {
          fontFamily: themeParam.typography.fontFamily,
        },
        h1: {
          ...themeParam.typography.h1,
        },
        h2: {
          ...themeParam.typography.h2,
        },
        h3: {
          ...themeParam.typography.h3,
        },
        h4: {
          ...themeParam.typography.h4,
        },
      }),
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          p: 'p',
          p2: 'p',
          p3: 'p',
          q: 'p',
        },
      },
    },
    //Overriding the default styles for the input label fields
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme, ownerState }) => {
          const isFilled = ownerState.variant === 'filled';

          return {
            fontFamily: 'Albert Sans',
            fontSize: HealthxchangeThemeFontSizes.desktop.s,
            color: theme.palette.darkGray.main,
            ...(isFilled ? {} : { top: '-6px' }),

            '&.Mui-focused, &.MuiInputLabel-shrink': {
              color: theme.palette.darkGray.main,
              ...(isFilled ? {} : { top: 0 }),
            },
            '&.Mui-error': {
              color: theme.palette.error.main,
            },
          };
        },
      },
    },
    //Overriding the default styles for the input fields value text
    MuiInputBase: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const isFilled = ownerState.variant === 'filled';
          const isMultiline = Boolean(ownerState.multiline);

          return {
            fontFamily: 'Albert Sans',
            fontSize: HealthxchangeThemeFontSizes.desktop.m,
            height: isMultiline ? 'auto' : isFilled ? 56 : theme.spacing(5),
            minHeight: isFilled ? 56 : undefined,
            textAlign: isFilled ? 'left' : 'center',
            justifyContent: isFilled ? 'flex-start' : 'center',
            alignItems: isMultiline ? 'flex-start' : 'center',
            backgroundColor: isFilled ? 'transparent' : 'white',
          };
        },
        input: ({ ownerState, theme }) => {
          const isFilled = ownerState.variant === 'filled';
          const isMultiline = Boolean(ownerState.multiline);

          return {
            height: isMultiline || isFilled ? 'auto' : theme.spacing(5),
            paddingTop: isFilled ? '26px !important' : '0 !important',
            paddingBottom: isFilled ? '6px !important' : '0 !important',
            paddingLeft: isFilled ? theme.spacing(1.5) : undefined,
            paddingRight: isFilled ? theme.spacing(1.5) : undefined,
            backgroundColor: 'transparent',
            borderRadius: 'inherit',
            boxSizing: 'border-box',
          };
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          height: ownerState.multiline ? 'auto' : 56,
          minHeight: ownerState.multiline ? undefined : 56,
          backgroundColor: theme.palette.common.white,
          borderRadius: theme.shape.borderRadius,
          overflow: 'hidden',
          alignItems: ownerState.multiline ? 'flex-start' : 'center',
          '&:hover, &.Mui-focused': {
            backgroundColor: theme.palette.common.white,
          },
          '&::before, &::after': {
            display: 'none',
          },
        }),
      },
    },
    //Overriding the default styles for the input fields borders
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          fontFamily: 'Albert Sans',
          height: ownerState.multiline ? 'auto' : theme.spacing(5),
          textAlign: 'left',
          borderRadius: theme.shape.borderRadius,
          overflow: 'hidden',
          backgroundColor: theme.palette.common.white,
          //Default colors
          '&.MuiInputBase-adornedEnd': {
            paddingRight: theme.spacing(2),
          },
          '.MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.palette.lightGray.main} !important`,
            borderWidth: '1px !important',
            borderRadius: 'inherit',
          },
          // Hover state (only when not disabled and not in error state)
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.palette.darkGray.main} !important`,
          },
          // Focused state (applies also when in error)
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.palette.darkGray.main} !important`,
            borderWidth: '1px !important',
          },
          // Error state, with stronger border
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.palette.red.main} !important`,
            borderWidth: '2px !important',
          },
          // Disabled state (override any other styles if present)
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.palette.lightGray.main} !important`, // Disabled color
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: ({ theme }) => ({
          marginRight: theme.spacing(1),
        }),
      },
    },
    //Overriding the default styles for the input fields helper / error text
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: HealthxchangeThemeFontSizes.desktop.xxs,
          fontFamily: 'Albert Sans',
        },
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          width: 'auto',
          fontFamily: 'Albert Sans',
          fontWeight: 500,
          boxShadow: 'none',
          ':disabled': {
            cursor: 'not-allowed',
            pointerEvents: 'all',
            backgroundColor: theme.palette.lightGray.main,
            color: theme.palette.white.main,
            borderColor: theme.palette.white.main,
          },
        }),
        sizeSmall: {
          fontSize: HealthxchangeThemeFontSizes.desktop.xs,
          lineHeight: '0.9099999999999999rem',
          minWidth: '12.rem',
          height: '2.25rem',
        },
        sizeMedium: {
          fontSize: HealthxchangeThemeFontSizes.desktop.s,
          lineHeight: '1.06125rem',
          minWidth: '7rem',
          height: '2.875rem',
        },
        sizeLarge: {
          fontSize: HealthxchangeThemeFontSizes.desktop.m,
          lineHeight: '1.213125rem',
          minWidth: '7rem',
          height: '3.5rem',
        },
        outlined: ({ theme, ownerState }) => {
          const buttonColour = ownerState.color as keyof typeof HealthxchangeThemeColours;
          const isValid = Object.keys(HealthxchangeThemeColours).includes(buttonColour);
          return {
            '&:hover:not(.Mui-disabled)': {
              backgroundColor: isValid
                ? blend(
                    theme.palette[buttonColour].main,
                    theme.palette[buttonColour].contrastText,
                    0.9,
                  )
                : undefined,
            },
          };
        },
        contained: ({ theme, ownerState }) => {
          const buttonColour = ownerState.color as keyof typeof HealthxchangeThemeColours;
          const isValid = Object.keys(HealthxchangeThemeColours).includes(buttonColour);
          return {
            border: 'none',
            '&:hover:not(.Mui-disabled)': {
              backgroundColor: isValid
                ? blend(
                    theme.palette[buttonColour].main,
                    theme.palette[buttonColour].contrastText,
                    buttonColour === 'black' ? 0.2 : 0.1,
                  )
                : undefined,
            },
          };
        },
        fullWidth: {
          width: '100%',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: ({ theme }) => menuListStyleOverrides(theme),
      },
    },
    MuiPickersToolbarText: {
      styleOverrides: {
        root: pickersToolbarTextStyleOverrides,
      },
    },
    MuiPickersToolbarButton: {
      styleOverrides: {
        root: pickersToolbarButtonStyleOverrides,
      },
    },
    MuiDateTimePickerToolbar: {
      styleOverrides: dateTimePickerToolbarStyleOverrides,
    },
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        root: ({ theme }) => multiSectionDigitalClockSectionStyleOverrides(theme),
      },
    },
  },
});

// Iterate through each of our healthxchange theme colours and add them to the palette
// in the format that MUI expects: main, light, dark and contrastText
// Also add desktop (md and up) typography overrides.
const theme = createTheme(defaultConfig, {
  palette: Object.entries(HealthxchangeThemeColours).reduce(
    (acc: Record<string, PaletteColor>, [key, value]) => {
      acc[key] = defaultConfig.palette.augmentColor({
        color: { main: value },
        name: key,
      });
      return acc;
    },
    {},
  ),
  typography: {
    ...buildDesktopResponsiveTypographyOverrides(defaultConfig),
  },
});

export {
  theme,
  TextVariant,
  HealthxchangeThemeColours,
  HealthxchangeThemeFontSizes,
  useTheme,
  Z_INDEX,
};
